import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Link2, Search, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { borderPrimary, primaryText, secondaryText, semiBoldText, smallTextSize, tertiaryText } from '../../../styling/stylingHelper';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../../ui/InventorySlideOver';
import { useProductSearch } from '../../../hooks/useProductSearch';
import { useWasteStore } from '../../../stores/appWasteStore';
import {
  dryRunWasteLinks,
  planMethod,
  WasteIngredientPlanRow,
  WasteLinkCommitSummary,
  WasteLinkDryRunSummary,
  WasteLinkResolution,
} from '../../../services/waste/wasteService';
import { WasteProductOption } from './wasteLinkShared';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
  /** How many ingredient entries are waiting to be linked — shown before the (paid) AI pass. */
  pendingCount: number;
  productOptions: WasteProductOption[];
}

// 'intro' states what will be analysed (the AI pass costs money, so it is one deliberate
// click); 'review' is the per-row accept/search/skip step; 'done' reports what landed.
type Step = 'intro' | 'review' | 'done';

/**
 * A row's state in the review step. `pending` is UI-only and is NOT sent on commit: an
 * untouched row must stay `unlinked` so a later run can retry it, whereas an explicit
 * Skip persists as `skipped` ("this will never be a product, stop asking"). Without the
 * third state, committing would mark every row nobody looked at as permanently skipped.
 */
type RowDecision = WasteLinkResolution | { wasteId: string; kind: 'ingredient'; action: 'pending' };

const SEARCH_LIMIT = 20;

/**
 * "Link waste to products" automation: bulk-links unlinked INGREDIENT waste entries to
 * order-history products. Mirrors the inventory `LinkProductsModal` flow (dry-run → review
 * → commit): confident matches arrive pre-bound, everything else waits for a human, so an
 * untouched review never links a guess. Re-running only ever touches entries still unlinked.
 *
 * Dishes never appear here — a dish resolves to its cost-price analysis by name, and one
 * without an analysis needs that cost price set up, not a link.
 */
export default function WasteLinkModal({ isOpen, onClose, restaurantId, pendingCount, productOptions }: Props) {
  const { t } = useTranslation();
  const commitLinks = useWasteStore((s) => s.commitLinks);

  const [step, setStep] = useState<Step>('intro');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [ingredientRows, setIngredientRows] = useState<WasteIngredientPlanRow[]>([]);
  const [summary, setSummary] = useState<WasteLinkDryRunSummary | null>(null);
  const [resolutions, setResolutions] = useState<Record<string, RowDecision>>({});
  const [commitSummary, setCommitSummary] = useState<WasteLinkCommitSummary | null>(null);

  const [searchOpenFor, setSearchOpenFor] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fresh state on every open — a previous run's plan is stale the moment links change.
  useEffect(() => {
    if (!isOpen) return;
    setStep('intro');
    setBusy(false);
    setError(null);
    setIngredientRows([]);
    setSummary(null);
    setResolutions({});
    setCommitSummary(null);
    setSearchOpenFor(null);
    setSearchTerm('');
  }, [isOpen]);

  const runDryRun = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await dryRunWasteLinks(restaurantId);
      setIngredientRows(res.ingredients);
      setSummary(res.summary);

      // Confident matches arrive pre-bound; everything else waits for a human, so an
      // untouched review never links a guess (and never skips one either).
      const next: Record<string, RowDecision> = {};
      for (const r of res.ingredients) {
        next[r.wasteId] =
          r.status === 'auto' && r.best
            ? {
                wasteId: r.wasteId,
                kind: 'ingredient',
                action: 'bind',
                platform: r.best.platform,
                articleId: r.best.articleId,
                productName: r.best.productName,
                confidence: r.best.confidence,
                method: planMethod(r.best),
              }
            : { wasteId: r.wasteId, kind: 'ingredient', action: 'pending' };
      }
      setResolutions(next);
      setStep('review');
    } catch (err) {
      console.error('Waste link dry-run failed:', err);
      setError(t('waste.link.errorAnalyze'));
    } finally {
      setBusy(false);
    }
  };

  const bound = useMemo(() => Object.values(resolutions).filter((r) => r.action === 'bind'), [resolutions]);
  /** Only decided rows are sent — `pending` ones stay unlinked and can be retried. */
  const decided = useMemo(
    () => Object.values(resolutions).filter((r): r is WasteLinkResolution => r.action !== 'pending'),
    [resolutions]
  );

  const runCommit = async () => {
    if (bound.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await commitLinks(restaurantId, decided);
      setCommitSummary(res);
      setStep('done');
    } catch (err) {
      console.error('Waste link commit failed:', err);
      setError(t('waste.link.errorCommit'));
    } finally {
      setBusy(false);
    }
  };

  const setResolution = (wasteId: string, r: RowDecision) =>
    setResolutions((prev) => ({ ...prev, [wasteId]: r }));

  const toggleSearchFor = (wasteId: string) => {
    setSearchOpenFor((prev) => (prev === wasteId ? null : wasteId));
    setSearchTerm('');
  };

  const productResults = useProductSearch({
    data: productOptions,
    keys: ['productName', 'searchableId'],
    query: searchTerm,
    limit: SEARCH_LIMIT,
  });

  // Progress counts every row the planner did NOT auto-bind — the decisions a human owes.
  const allRows = useMemo(
    () => ingredientRows.map((r) => ({ wasteId: r.wasteId, status: r.status })),
    [ingredientRows]
  );
  const reviewTotal = allRows.filter((r) => r.status !== 'auto').length;
  const reviewDone = allRows.filter(
    (r) => r.status !== 'auto' && resolutions[r.wasteId] && resolutions[r.wasteId].action !== 'pending'
  ).length;
  const reviewPct = reviewTotal > 0 ? Math.round((reviewDone / reviewTotal) * 100) : 100;

  /** Row chrome: header, bound line, suggestions, manual search. */
  const renderRow = (args: {
    wasteId: string;
    itemName: string;
    status: string;
    confidence?: number;
    boundLabel: string | null;
    suggestions: Array<{ id: string; label: string; sublabel?: string; bind: () => void }>;
  }) => {
    const res = resolutions[args.wasteId];
    const isSkipped = res?.action === 'skip';
    return (
      <div key={args.wasteId} className={cn('p-3 rounded-xl border', borderPrimary)}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={cn(smallTextSize, semiBoldText, primaryText, 'truncate')}>{args.itemName}</p>
            <p className={cn('text-[11px]', tertiaryText)}>
              {t(`waste.link.status_${args.status}`)}
              {typeof args.confidence === 'number' && args.confidence < 1 && (
                <span className="ml-1 text-amber-600 dark:text-amber-400">
                  · {t('waste.link.confidence', { pct: Math.round(args.confidence * 100) })}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => toggleSearchFor(args.wasteId)}
              className={cn(
                'text-[11px] px-2 py-1 rounded-lg border flex items-center gap-1',
                borderPrimary,
                searchOpenFor === args.wasteId
                  ? 'bg-darkGreenColor/10 dark:bg-darkGreenColor-dark/10 text-darkGreenColor dark:text-darkGreenColor-dark'
                  : secondaryText,
                'hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
            >
              <Search className="w-3 h-3" /> {t('waste.link.bind')}
            </button>
            <button
              type="button"
              onClick={() => setResolution(args.wasteId, { wasteId: args.wasteId, kind: 'ingredient', action: 'skip', method: 'manual' })}
              className={cn(
                'text-[11px] px-2 py-1 rounded-lg border',
                borderPrimary,
                isSkipped ? 'bg-gray-100 dark:bg-gray-700 ' + primaryText : secondaryText,
                'hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
            >
              {t('waste.link.skip')}
            </button>
          </div>
        </div>

        {args.boundLabel && (
          <div className="mt-2 flex items-center justify-between gap-2 p-1.5 pl-2 rounded-lg bg-green-50 dark:bg-green-900/20">
            <p className="min-w-0 text-[11px] text-green-700 dark:text-green-300 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 shrink-0" />
              <span className="truncate">{args.boundLabel}</span>
            </p>
            {/* Rejecting a proposed match only clears it — saying "never link this" is
                the explicit Skip button, not this X. */}
            <button
              type="button"
              onClick={() => setResolution(args.wasteId, { wasteId: args.wasteId, kind: 'ingredient', action: 'pending' })}
              className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-800/40 shrink-0"
              aria-label={t('waste.link.clearMatch') as string}
            >
              <X className="w-3 h-3 text-green-700 dark:text-green-300" />
            </button>
          </div>
        )}

        {args.suggestions.length > 0 && (
          <div className="mt-2 space-y-1">
            {args.suggestions.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={s.bind}
                className={cn('block w-full text-left px-2 py-1 rounded-lg border', borderPrimary, 'hover:bg-blue-50 dark:hover:bg-blue-900/20')}
              >
                <span className="text-[11px] text-blue-600 dark:text-blue-400">+ {s.label}</span>
                {s.sublabel && <span className={cn('block text-[10px]', tertiaryText)}>{s.sublabel}</span>}
              </button>
            ))}
          </div>
        )}

        {searchOpenFor === args.wasteId && (
          <div className="mt-3 p-3 rounded-xl border bg-gray-50/60 dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-textQuaternary-dark" />
              <input
                autoFocus
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('waste.link.searchPlaceholder') as string}
                className={cn(
                  'w-full pl-9 pr-3 py-2 rounded-xl border text-sm',
                  borderPrimary,
                  'bg-white dark:bg-zinc-900',
                  primaryText,
                  'focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500'
                )}
              />
            </div>
            <div className="mt-2 max-h-44 overflow-y-auto">
              {productResults.results.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => {
                    setResolution(args.wasteId, {
                      wasteId: args.wasteId,
                      kind: 'ingredient',
                      action: 'bind',
                      platform: p.platform,
                      articleId: p.articleId,
                      productName: p.productName,
                      method: 'manual',
                    });
                    setSearchOpenFor(null);
                  }}
                  className="block w-full text-left px-3 py-2 mb-1 rounded-lg bg-white dark:bg-zinc-900 border border-transparent dark:border-transparent hover:border-darkGreenColor/50 dark:hover:border-darkGreenColor-dark/50 transition-colors"
                >
                  <span className={cn('text-sm', secondaryText)}>{p.productName}</span>
                  <span className={cn('block text-[10px]', tertiaryText)}>{p.platform}</span>
                </button>
              ))}
              {productResults.results.length === 0 && (
                <p className={cn(smallTextSize, tertiaryText, 'text-center py-4')}>{t('waste.link.noCandidates')}</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const getFooter = () => {
    if (step === 'intro') {
      return (
        <>
          <button type="button" onClick={onClose} className={slideOverCancelClass}>
            {t('waste.link.cancel')}
          </button>
          <button type="button" onClick={runDryRun} disabled={busy || pendingCount === 0} className={slideOverPrimaryClass}>
            {busy ? t('waste.link.analyzing') : t('waste.link.analyze')}
          </button>
        </>
      );
    }
    if (step === 'review') {
      return (
        <>
          <button type="button" onClick={() => setStep('intro')} disabled={busy} className={slideOverCancelClass}>
            {t('waste.link.back')}
          </button>
          <button type="button" onClick={runCommit} disabled={busy || bound.length === 0} className={slideOverPrimaryClass}>
            {busy ? t('waste.link.linking') : t('waste.link.linkCount', { n: bound.length })}
          </button>
        </>
      );
    }
    return (
      <button type="button" onClick={onClose} className={slideOverPrimaryClass}>
        {t('waste.link.close')}
      </button>
    );
  };

  return (
    <InventorySlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('waste.link.title')}
      description={t('waste.link.description')}
      footer={getFooter()}
      closeLabel={t('waste.link.cancel') as string}
    >
      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <p className={cn(smallTextSize, 'text-red-700 dark:text-red-300')}>{error}</p>
        </div>
      )}

      {step === 'intro' && (
        <div className={cn('p-4 rounded-xl border', borderPrimary)}>
          <p className={cn(smallTextSize, primaryText, semiBoldText)}>{t('waste.link.introCount', { n: pendingCount })}</p>
          <p className={cn('mt-2 text-[13px] leading-5', secondaryText)}>{t('waste.link.introBody')}</p>
        </div>
      )}

      {step === 'review' && summary && (
        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { label: t('waste.link.summaryTotal'), value: summary.total },
              { label: t('waste.link.summaryAuto'), value: summary.auto },
              { label: t('waste.link.summaryReview'), value: summary.review },
              { label: t('waste.link.summaryUnmatched'), value: summary.unmatched },
            ].map((chip, i) => (
              <span key={i} className={cn(smallTextSize, 'px-3 py-1.5 rounded-lg border', borderPrimary, secondaryText)}>
                {chip.label}: <span className={cn(semiBoldText, primaryText)}>{chip.value}</span>
              </span>
            ))}
          </div>

          {reviewTotal > 0 && (
            <div className="sticky top-0 z-10 -mx-6 px-6 py-3 mb-4 bg-card dark:bg-card-dark border-b border-zinc-200 dark:border-white/20">
              <div className="flex items-center justify-between mb-1.5">
                <span className={cn(smallTextSize, semiBoldText, primaryText)}>
                  {t('waste.link.reviewProgress', { done: reviewDone, total: reviewTotal })}
                </span>
                <span className={cn('text-[11px]', tertiaryText)}>
                  {reviewDone === reviewTotal ? t('waste.link.reviewAllDone') : `${reviewPct}%`}
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
                <div
                  className="h-full rounded-full bg-darkGreenColor dark:bg-darkGreenColor-dark transition-all duration-300"
                  style={{ width: `${reviewPct}%` }}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            {ingredientRows.map((r) => {
              const res = resolutions[r.wasteId];
              const boundName = res?.action === 'bind' ? res.productName ?? null : null;
              return renderRow({
                wasteId: r.wasteId,
                itemName: r.itemName,
                status: r.status,
                confidence: r.best?.confidence,
                boundLabel: boundName,
                suggestions: r.suggestions
                  .filter((s) => s.productName !== boundName)
                  .map((s) => ({
                    id: `${s.platform}:${s.articleId ?? ''}:${s.productName}`,
                    label: s.productName,
                    // Supplier only: `unitPrice` is reserved for Phase 2 and never populated
                    // here, and a raw order-line price would be pre-factuur-override anyway.
                    sublabel: s.supplier ?? s.platform,
                    bind: () =>
                      setResolution(r.wasteId, {
                        wasteId: r.wasteId,
                        kind: 'ingredient',
                        action: 'bind',
                        platform: s.platform,
                        articleId: s.articleId,
                        productName: s.productName,
                        method: 'manual',
                      }),
                  })),
              });
            })}
          </div>

          {allRows.length === 0 && (
            <p className={cn(smallTextSize, tertiaryText, 'text-center py-6')}>{t('waste.link.nothingToLink')}</p>
          )}
        </div>
      )}

      {step === 'done' && commitSummary && (
        <div className={cn('p-4 rounded-xl border', borderPrimary)}>
          <p className={cn(smallTextSize, semiBoldText, primaryText, 'flex items-center gap-2')}>
            <Link2 className="w-4 h-4 text-darkGreenColor dark:text-darkGreenColor-dark" />
            {t('waste.link.doneLinked', { n: commitSummary.linked })}
          </p>
          <div className="mt-3 space-y-1">
            {[
              { label: t('waste.link.doneSkipped'), value: commitSummary.skipped },
              { label: t('waste.link.doneFailed'), value: commitSummary.failed },
            ]
              .filter((x) => x.value > 0)
              .map((x, i) => (
                <p key={i} className={cn('text-[12px]', secondaryText)}>
                  {x.label}: <span className={semiBoldText}>{x.value}</span>
                </p>
              ))}
          </div>
        </div>
      )}
    </InventorySlideOver>
  );
}
